import { createContext, useContext, type ReactNode } from "react";
import type { User } from "../types";

// AppContext is the role-aware bag of currentUser + domain actions handed down
// from App.tsx. We type the data fields strictly so role/auth-derived code is
// safe, but the 25+ domain action callables (showSuccess, createJob,
// updateAgency, ...) remain `any` here. Each action is already strictly typed
// at its source hook (useJobs, useUsers, ...); typing them again at the
// context boundary would require duplicating those signatures. Consumers
// reaching for an action get `any` — equivalent to the pre-context
// destructure — which keeps the migration incremental.
export interface AppContextValue {
  currentUser: User | null;
  agencyId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const AppContext = createContext<AppContextValue | null>(null);

interface AppProviderProps {
  value: AppContextValue;
  children: ReactNode;
}

export function AppProvider({ value, children }: AppProviderProps) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within AppProvider");
  return ctx;
}
